import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Form, Input, InputNumber, Modal, Select, Switch, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Search } from 'lucide-react';
import Layout from '../../Layout';
import { khoApi, useKho } from './khoStore';
import { ITEM_KIND, fmtMoney, fmtNum, fmtQty, itemStocks, supplierById, type Item, type ItemKind, type ItemStock } from './khoLogic';
import { Badge, ItemFlags, KindTag, PageHeader, itemUrl, useKhoPerms } from './khoUi';

type FormValues = Omit<Item, 'id'>;
const money = {
  formatter: (v: number | string | undefined) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
  parser: (v: string | undefined) => Number((v || '').replace(/\./g, '')),
};

/** Danh mục thuốc, vật tư, mỹ phẩm, đầu tip — quy cách, tồn tối thiểu, bảo quản */
export default function ItemsPage() {
  const s = useKho();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canEdit, canCreate } = useKhoPerms();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<ItemKind>();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const [form] = Form.useForm<FormValues>();
  const now = Date.now();

  // Mở form sửa từ trang chi tiết: /warehouse-items?edit=<id>
  useEffect(() => {
    const id = params.get('edit');
    const it = id && s.items.find(i => i.id === id);
    if (it) { setEditing(it); const n = new URLSearchParams(params); n.delete('edit'); setParams(n, { replace: true }); }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editing === 'new') form.setFieldsValue({ code: '', name: '', kind: 'consumable', unit: 'Cái', minStock: 0, cost: 0, active: true, packUnit: undefined, packSize: undefined, cold: false, controlled: false } as FormValues);
    else if (editing) form.setFieldsValue(editing);
  }, [editing, form]);

  const stocks = itemStocks(s, now);
  const rows = stocks.filter(r => {
    if (!showInactive && !r.item.active) return false;
    if (kind && r.item.kind !== kind) return false;
    const k = q.trim().toLowerCase();
    return !k || r.item.name.toLowerCase().includes(k) || r.item.code.toLowerCase().includes(k) || (r.item.brand || '').toLowerCase().includes(k);
  }).sort((a, b) => a.item.kind.localeCompare(b.item.kind) || a.item.code.localeCompare(b.item.code));

  const submit = async () => {
    const v = await form.validateFields();
    const r = khoApi.saveItem({ ...v, id: editing && editing !== 'new' ? editing.id : undefined, packSize: v.packUnit ? v.packSize : undefined });
    if (!r.ok) { message.error(r.errors?.[0]); return; }
    message.success('Đã lưu mặt hàng');
    setEditing(null);
  };

  const columns: ColumnsType<ItemStock> = [
    {
      title: 'Mặt hàng', key: 'name', render: (_, r) => (
        <div className={r.item.active ? '' : 'opacity-50'}>
          <span className="font-medium">{r.item.name}</span><ItemFlags item={r.item} />
          <div className="text-[11px] text-gray-400">{r.item.code}{r.item.brand ? ` · ${r.item.brand}` : ''}{r.item.active ? '' : ' · ngừng sử dụng'}</div>
        </div>
      ),
    },
    { title: 'Loại', key: 'kind', width: 160, render: (_, r) => <KindTag item={r.item} /> },
    { title: 'Quy cách', key: 'pack', width: 170, render: (_, r) => (r.item.packUnit && r.item.packSize ? `1 ${r.item.packUnit} = ${fmtNum(r.item.packSize)} ${r.item.unit}` : r.item.unit) },
    { title: 'Tồn / tối thiểu', key: 'stock', align: 'right', width: 210, render: (_, r) => (
      <div><span className={r.low ? 'text-orange-600 font-semibold' : ''}>{fmtQty(r.item, r.qty)}</span>
        {r.item.minStock > 0 && <div className="text-[11px] text-gray-400">tối thiểu {fmtNum(r.item.minStock)}</div>}</div>
    ) },
    { title: 'Giá vốn / đơn vị', key: 'cost', align: 'right', width: 140, render: (_, r) => fmtMoney(r.item.cost) },
    { title: 'Giá bán', key: 'price', align: 'right', width: 120, render: (_, r) => (r.item.price ? fmtMoney(r.item.price) : <span className="text-gray-300">—</span>) },
    { title: 'Nhà cung cấp', key: 'sup', width: 200, render: (_, r) => <span className="text-xs text-gray-500">{supplierById(s, r.item.supplierId)?.name || '—'}</span> },
    {
      title: '', key: 'act', width: 150, render: (_, r) => (
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          {canEdit && <Button size="small" onClick={() => setEditing(r.item)}>Sửa</Button>}
          {canEdit && <Button size="small" type="text" onClick={() => {
            if (r.item.active && r.qty > 0) { message.warning('Còn tồn kho — xuất hết hoặc hủy trước khi ngừng sử dụng'); return; }
            khoApi.setItemActive(r.item.id, !r.item.active);
          }}>{r.item.active ? 'Ngừng' : 'Dùng lại'}</Button>}
        </div>
      ),
    },
  ];

  const kindNow = Form.useWatch('kind', form);
  const packUnit = Form.useWatch('packUnit', form);

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Danh mục vật tư" desc="Thuốc, vật tư tiêu hao, mỹ phẩm, đầu tip máy — quy đổi đơn vị, tồn tối thiểu, bảo quản lạnh, thuốc kiểm soát"
          extra={(canCreate || canEdit) && <Button type="primary" icon={<Plus size={14} />} onClick={() => setEditing('new')}>Thêm mặt hàng</Button>} />

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm">
          <div className="p-4 flex flex-wrap items-center gap-2">
            <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, mã, thương hiệu" value={q} onChange={e => setQ(e.target.value)} />
            <Select allowClear className="w-full sm:w-[190px]" placeholder="Loại hàng" value={kind} onChange={setKind}
              options={(Object.keys(ITEM_KIND) as ItemKind[]).map(k => ({ value: k, label: ITEM_KIND[k].label }))} />
            <Checkbox checked={showInactive} onChange={e => setShowInactive(e.target.checked)}>Hiện hàng ngừng sử dụng</Checkbox>
            <span className="ml-auto flex gap-1 flex-wrap">
              {(Object.keys(ITEM_KIND) as ItemKind[]).map(k => <Badge key={k} className={ITEM_KIND[k].badge}>{ITEM_KIND[k].label}: {s.items.filter(i => i.kind === k && i.active).length}</Badge>)}
            </span>
          </div>
          <Table<ItemStock> rowKey={r => r.item.id} size="middle" columns={columns} dataSource={rows} scroll={{ x: 1150 }} pagination={{ pageSize: 25, hideOnSinglePage: true }}
            rowClassName="cursor-pointer" onRow={r => ({ onClick: () => navigate(itemUrl(r.item.id)) })} />
        </div>
      </div>

      <Modal open={!!editing} width={720} title={editing === 'new' ? 'Thêm mặt hàng' : `Sửa ${editing ? (editing as Item).name : ''}`}
        okText="Lưu" cancelText="Đóng" onCancel={() => setEditing(null)} onOk={submit} forceRender>
        <Form form={form} layout="vertical" requiredMark className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Form.Item name="code" label="Mã hàng" rules={[{ required: true, message: 'Nhập mã' }]}><Input placeholder="VD: BTX100" /></Form.Item>
          <Form.Item name="name" label="Tên hàng" rules={[{ required: true, message: 'Nhập tên' }]}><Input /></Form.Item>
          <Form.Item name="kind" label="Loại hàng" rules={[{ required: true }]}>
            <Select options={(Object.keys(ITEM_KIND) as ItemKind[]).map(k => ({ value: k, label: ITEM_KIND[k].label }))} />
          </Form.Item>
          <Form.Item name="brand" label="Thương hiệu / hãng"><Input /></Form.Item>
          <Form.Item name="unit" label="Đơn vị xuất (nhỏ nhất)" rules={[{ required: true, message: 'Nhập đơn vị' }]} tooltip="Đơn vị dùng khi xuất cho dịch vụ và tính tồn: Unit, ml, Ống, Cái, Shot…">
            <Input placeholder="Unit, ml, Ống, Cái, Shot…" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-2">
            <Form.Item name="packUnit" label="Đơn vị đóng gói" tooltip="Đơn vị khi mua: Lọ, Hộp, Cartridge…"><Input placeholder="Lọ, Hộp…" allowClear /></Form.Item>
            <Form.Item name="packSize" label="Quy đổi" rules={[{ required: !!packUnit, message: 'Nhập số' }]}>
              <InputNumber className="!w-full" min={1} disabled={!packUnit} placeholder="1 gói = ?" />
            </Form.Item>
          </div>
          <Form.Item name="minStock" label="Tồn tối thiểu (đơn vị xuất)" tooltip="Tổng tồn mọi kho dưới mức này sẽ cảnh báo đặt hàng"><InputNumber className="!w-full" min={0} /></Form.Item>
          <Form.Item name="supplierId" label="Nhà cung cấp chính">
            <Select allowClear options={s.suppliers.map(x => ({ value: x.id, label: x.name }))} />
          </Form.Item>
          <Form.Item name="cost" label="Giá vốn mặc định / đơn vị xuất"><InputNumber className="!w-full" min={0} {...money} suffix="₫" /></Form.Item>
          <Form.Item name="price" label="Giá bán lẻ / đơn vị" tooltip="Dùng cho mỹ phẩm bán tại quầy">
            <InputNumber className="!w-full" min={0} {...money} suffix="₫" disabled={kindNow !== 'cosmetic'} />
          </Form.Item>
          <div className="md:col-span-2 flex flex-wrap gap-6">
            <Form.Item name="cold" valuePropName="checked" className="!mb-2"><Checkbox>Bảo quản lạnh 2–8°C</Checkbox></Form.Item>
            <Form.Item name="controlled" valuePropName="checked" className="!mb-2"><Checkbox>Thuốc kiểm soát (xuất phải duyệt chuyên môn)</Checkbox></Form.Item>
            <Form.Item name="active" valuePropName="checked" label="Đang sử dụng" className="!mb-2"><Switch size="small" /></Form.Item>
          </div>
          <Form.Item name="note" label="Ghi chú" className="md:col-span-2"><Input.TextArea autoSize={{ minRows: 2 }} placeholder="Cách bảo quản, lưu ý khi dùng…" /></Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
